type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const PWA_INSTALLED_KEY = "oversight-pwa-installed";

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();

const notify = () => {
  subscribers.forEach((callback) => callback());
};

const setInstalledFlag = (installed: boolean) => {
  if (typeof window === "undefined") {
    return;
  }

  if (installed) {
    localStorage.setItem(PWA_INSTALLED_KEY, "true");
  } else {
    localStorage.removeItem(PWA_INSTALLED_KEY);
  }
  notify();
};

export const initPwaInstallListener = () => {
  if (typeof window === "undefined") {
    return;
  }

  const flaggedWindow = window as Window & { __pwaInstallInit?: boolean };
  if (flaggedWindow.__pwaInstallInit) {
    return;
  }
  flaggedWindow.__pwaInstallInit = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    setInstalledFlag(false);
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    setInstalledFlag(true);
  });
};

export const getDeferredPrompt = () => deferredPrompt;

export const clearDeferredPrompt = () => {
  deferredPrompt = null;
  notify();
};

export const markPwaInstalled = () => {
  deferredPrompt = null;
  setInstalledFlag(true);
};

export const subscribePwaInstall = (callback: () => void) => {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
};

export const isPwaInstalled = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true;

  return standalone || localStorage.getItem(PWA_INSTALLED_KEY) === "true";
};

export const isIosDevice = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
};
